import { Injectable } from '@angular/core';
import { LoggingService } from '@shared/logging';
import { RxDatabase, RxJsonSchema } from 'rxdb';
import { MigrationManagerService } from '../migration';
import { WorkspaceDatabaseDbContext, WorkspaceDatabaseKeys } from '../schemas';
import { addRxPlugin } from 'rxdb';
import { RxDBMigrationPlugin } from 'rxdb/plugins/migration';
import { RxDBLocalDocumentsPlugin } from 'rxdb/plugins/local-documents';
addRxPlugin(RxDBMigrationPlugin);
addRxPlugin(RxDBLocalDocumentsPlugin);

@Injectable()
export class DynamicSchemaService {
	public constructor(
		private logggingService: LoggingService,
		private migrationManagerService: MigrationManagerService
	) {}

	public async saveToLocal(
		database: RxDatabase<WorkspaceDatabaseDbContext>,
		tableName: string,
		schema: RxJsonSchema<any>
	): Promise<void> {
		await database.insertLocal(tableName, { schema });
	}

	public async upsertToLocal(
		database: RxDatabase<WorkspaceDatabaseDbContext>,
		tableName: string,
		schema: RxJsonSchema<any>
	): Promise<void> {
		try {
			await database.upsertLocal(tableName, { schema });
		} catch (error) {
			throw new Error('Inserting new local schema failed');
		}
	}

	public async checkIfMigrationIsNeeded(database: RxDatabase<WorkspaceDatabaseDbContext>) {
		const dataTableNames: string[] = (await database.tables.find().exec()).map((tables) =>
			tables.table_name.toLowerCase()
		);

		if (dataTableNames.length === 0) {
			throw new Error('There are no tables stored in the the Database. Please make sure import was successfull');
		}

		const firstTable = dataTableNames[0];
		const localDoc = await database.getLocal(firstTable);
		const currentSchema: RxJsonSchema<any> = localDoc.get(WorkspaceDatabaseKeys.schema);

		return currentSchema.version < MigrationManagerService.currentDatabaseVersion;
	}

	public async resetDataTablesCollectionsFromLocal(
		database: RxDatabase<WorkspaceDatabaseDbContext>
	): Promise<boolean> {
		this.logggingService.info('DynamicSchemaService', `resetDataTablesCollectionsFromLocal`);
		const dataTableNames: string[] = (await database.tables.find().exec()).map((tables) =>
			tables.table_name.toLowerCase()
		);
		let migrationWasApplied = true;

		const firstTable = dataTableNames[0];
		if (database.collections[firstTable]) {
			return false;
		}

		for (const tableName of dataTableNames) {
			const collections: any = {};

			this.logggingService.info('DynamicSchemaService', `reset ${tableName} from local`);
			const localDoc = await database.getLocal(tableName);
			try {
				const currentSchema: RxJsonSchema<any> = localDoc.get(WorkspaceDatabaseKeys.schema);
				const migratedCollection = this.migrationManagerService.applyMigration(currentSchema);
				collections[tableName] = migratedCollection;
				if (currentSchema.version !== migratedCollection.schema.version) {
					await this.upsertToLocal(database, tableName, migratedCollection.schema);
				} else {
					migrationWasApplied = false;
				}
				// eslint-disable-next-line no-debugger
				const addedCollection = await database.addCollections(collections);
				await addedCollection[tableName].migratePromise(10);
			} catch (error) {
				// eslint-disable-next-line no-debugger
				// there is a random javascript error from the plugin
				this.logggingService.error('DynamicSchemaService', error);
				return migrationWasApplied;
			}
		}
		return migrationWasApplied;
	}
}
